import { CommonModule } from '@angular/common';
import { Component, ElementRef, HostListener, OnInit, ViewChild, OnDestroy } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { LangChangeEvent, TranslateModule, TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { FileManagementService } from '../../shared/services/file-management.service';
import { FileNavigationService } from '../../shared/services/file-navigation.service';
import { SearchService } from '../../shared/services/search.service';
import { PermissionsTabComponent } from '../../shared/components/permissions-tab/permissions-tab.component';
import { Folder, FileItem, FileManagementHelper, BrowseFolderResponse } from '../../shared/models/file-management.model';
import { FilePermission } from '../../shared/models/permissions.model';

@Component({
  selector: 'app-file-management',
  templateUrl: './file-management.html',
  styleUrls: ['./file-management.scss'],
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TranslateModule,
    PermissionsTabComponent
  ],
})
export class FileManagementComponent implements OnInit, OnDestroy {
  @ViewChild('contextMenuRef') contextMenuRef!: ElementRef;
  @ViewChild('folderMenuInfo') folderMenuInfo!: ElementRef;
  @ViewChild('fileMenuInfo') fileMenuInfo!: ElementRef;
  @ViewChild('permissionsTab') permissionsTab!: PermissionsTabComponent;

  private destroy$ = new Subject<void>();
  
  isInternal: boolean = false; // Master switch for edit/create/delete operations
  userRole = 'Super Admin';
  selectedModule: any = { id: 1, nameEn: 'Library', nameAr: 'مكتبة' }; // Initialize with default
  activeRootFolderId: number | null = null; // Track active root folder for persistent highlighting
  currentPath: Folder[] = [];
  showCreateFolder = false;
  showUploadFile = false;
  viewMode: 'grid' | 'list' = 'grid';
  currentFolderId: number | null = null;
  rootFolders: Folder[] = [];
  isLoading: boolean = false;
  apiErrors: string[] = [];

  contextMenu = {
    visible: false,
    x: 0,
    y: 0,
    folder: null as Folder | null
  };

  pressTimer: any;

  folderForm = {
    nameEn: '',
    nameAr: '',
    descriptionEn: '',
    descriptionAr: ''
  };

  fileForm: any = {
    titleEn: '',
    titleAr: '',
    descriptionEn: '',
    descriptionAr: '',
    documentType: '',
    language: '',
    uploadDate: new Date().toISOString().split('T')[0],
    file: undefined
  };

  // Multi-step form properties
  uploadStep: number = 1; // 1 for info, 2 for permissions

  // Data will be loaded from API
  folders: Folder[] = [];
  files: FileItem[] = [];
  expandedFolders: number[] = [];

  // Cached data for performance
  cachedCurrentFolders: Folder[] = [];
  cachedCurrentFiles: FileItem[] = [];

  // Pagination and Search properties
  searchTerm: string = '';
  pageSize: number = 12;
  currentPage: number = 1;
  // Server-provided pagination info (when browsing via API)
  serverTotalCount: number | null = null;
  serverTotalPages: number | null = null;

  hoveredFolder: any = null;
  hoveredFile: any = null;

  currentLanguage: 'en' | 'ar' | string = 'en';

  constructor(
    private translate: TranslateService,
    private fileManagementService: FileManagementService,
    private fileNavigationService: FileNavigationService
    , private searchService: SearchService
  ) { }

  ngOnInit(): void {
    this.ensureAccess();
    // Default to list view on small/mobile screens
    if (this.isMobileScreen()) {
      this.viewMode = 'list';
    }
    
    // Set initial language from translate service
    this.currentLanguage = this.translate.currentLang || this.translate.defaultLang || 'en';
    
    // Subscribe to future language changes
    this.translate.onLangChange
      .pipe(takeUntil(this.destroy$))
      .subscribe((event: LangChangeEvent) => {
        this.currentLanguage = event.lang;
      });

    // Subscribe to file navigation events from notifications
    this.fileNavigationService.fileNavigation$
      .pipe(takeUntil(this.destroy$))
      .subscribe((navData) => {
        this.navigateToFileInFolder(navData.fileId, navData.file, navData.breadcrumb, navData.parentFolderId);
      });

    // Subscribe to folder selection events from sidebar or explorer
    this.fileNavigationService.folderSelection$
      .pipe(takeUntil(this.destroy$))
      .subscribe(({ folder, breadcrumb }) => {
        // Sync sidebar tree: expand all ancestors and set selection
        this.expandFolderPath(folder.id);
        this.currentFolderId = folder.id;
        // Build path from folder to root for sidebar selection
        const path: Folder[] = [];
        let currentId: number | null = folder.id;
        while (currentId) {
          const f = this.folders.find(f => f.id === currentId);
          if (f) {
            path.unshift(f);
            currentId = f.parentId;
          } else break;
        }
        this.currentPath = path;
        // Set selected module and active root if root
        if (path.length && path[0].parentId == null) {
          this.selectedModule = path[0];
          this.activeRootFolderId = path[0].id;
        }
        // Lazy load folder contents
        // Reset pagination to first page when a new folder is selected
        this.currentPage = 1;
        // Clear any client-side cached page items to avoid showing stale items
        this.cachedCurrentFolders = [];
        this.cachedCurrentFiles = [];
        this.browseFolderFromAPI(folder.id);
      });

    // Initialize with first root folder
    if (this.rootFolders.length === 0) {
      this.init();
    } else {
      this.selectedModule = this.rootFolders[0];
      this.navigateToFolder(this.selectedModule.id);
    }

    // Subscribe to global search terms (from header)
    this.searchService.search$.pipe(takeUntil(this.destroy$)).subscribe(term => {
      if (term !== undefined && term !== null) {
        this.onSearchChange(term);
      }
    });
  }

  private isMobileScreen(): boolean {
    try {
      return typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(max-width: 768px)').matches;
    } catch (e) {
      return false;
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Handle single click on a folder. On touch/mobile devices we navigate on single tap.
   * On desktop we keep the existing double-click behavior.
   */
  handleFolderClick(event: MouseEvent, folderId: number): void {
    try {
      if (this.isMobileScreen() || this.isTouchDevice()) {
        // prevent parent click handlers from interfering
        event.stopPropagation();
        this.navigateToFolder(folderId);
      }
    } catch (e) {
      // fallback: do nothing
    }
  }

  private isTouchDevice(): boolean {
    try {
      return typeof window !== 'undefined' && ('ontouchstart' in window || navigator.maxTouchPoints > 0);
    } catch (e) {
      return false;
    }
  }

  /**
   * Initialize component - Load root folders from API
   */
  init(): void {
    this.loadRootFolders();
  }

  /**
   * Load root folders from API with pagination
   */
  loadRootFolders(): void {
    this.isLoading = true;
    this.apiErrors = [];

    this.fileManagementService.getRootFolders(this.currentPage, this.pageSize)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          if (response.success) {
            this.rootFolders = response.data.folders;
            
            // Add root folders to folders array for tree navigation
            this.folders.push(...this.rootFolders);

            if (this.rootFolders.length > 0) {
              // Select and browse first root folder by default
              this.selectedModule = this.rootFolders[0];
              this.activeRootFolderId = this.selectedModule.id; // Set as active root
              this.currentPath = [this.selectedModule];
              this.browseFolderFromAPI(this.selectedModule.id);
            } else {
              this.isLoading = false;
            }
          } else {
            console.warn('Root folders API returned success=false');
            this.isLoading = false;
          }
        },
        error: (err) => {
          console.error('Failed to load root folders:', err);
          this.apiErrors = ['Failed to load root folders. Please try again.'];
          this.isLoading = false;
        }
      });
  }

  /**
   * Browse a specific folder from API with lazy loading
   * Only loads subfolders and files (folder is already selected)
   */
  browseFolderFromAPI(folderId: number, keyword?: string | null): void {
    this.isLoading = true;
    // Ensure currentFolderId is set so filtering displays API results correctly
    this.currentFolderId = folderId;
    this.apiErrors = [];

    // Clear local cached page items while loading
    this.cachedCurrentFolders = [];
    this.cachedCurrentFiles = [];

    this.fileManagementService.browseFolder(folderId, this.currentPage, this.pageSize, keyword)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: BrowseFolderResponse) => {
          if (response.success) {
            // Only process subfolders and files
            const subFolders = response.data.subFolders || [];
            const files = response.data.files || [];

            // Store pagination info
            this.currentPage = response.data.pageNumber;
            this.pageSize = response.data.pageSize;
            // Use server totals when provided so pagination reflects API
            this.serverTotalCount = response.data.totalCount ?? null;
            this.serverTotalPages = response.data.totalPages ?? null;

            // Update folders array with subfolders (for tree view)
            this.folders = this.folders.filter(f => f.parentId !== folderId);
            this.folders.push(...subFolders);

            // Update files with the API response
            this.files = this.files.filter(f => f.folderId !== folderId);
            this.files.push(...files);

            // If server-side paging is used, display the returned page items directly
            if (this.serverTotalCount != null) {
              this.cachedCurrentFolders = subFolders;
              this.cachedCurrentFiles = files;
            }

            // Handle API errors if present
            if (response.errors && response.errors.length > 0) {
              this.apiErrors = response.errors;
              console.warn('API returned errors:', response.errors);
            }
          } else {
            console.warn('Browse folder API returned success=false');
          }
          this.isLoading = false;
          this.refreshCachedData();
        },
        error: (err: any) => {
          console.error('Failed to browse folder:', err);
          this.apiErrors = ['Failed to load folder contents. Please try again.'];
          this.isLoading = false;
        }
      });
  }

  onFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      const file = input.files[0];
      const maxSize = 5 * 1024 * 1024; // 5MB

      // Check file size
      if (file.size > maxSize) {
        this.apiErrors = [`File size exceeds 5MB limit. Selected file is ${(file.size / (1024 * 1024)).toFixed(2)}MB`];
        input.value = ''; // Clear the file input
        return;
      }

      // Clear any previous file size errors
      if (this.apiErrors.some(e => e.includes('File size'))) {
        this.apiErrors = this.apiErrors.filter(e => !e.includes('File size'));
      }

      (this.fileForm as any).file = file;
    }
  }

  ensureAccess() {
    if (!this.hasAccess((this.selectedModule as any).nameEn)) {
      const accessibleModules = this.permissionsForRole(this.userRole);
      this.selectedModule = accessibleModules[0];
      this.currentPath = [];
    }
  }

  permissionsForRole(role: string): string[] {
    const permissions: Record<string, string[]> = {
      'Super Admin': ['Library', 'National Programs', 'Security Directives'],
      'Internal User': ['Library', 'National Programs'],
      'External User': ['Library']
    };
    return permissions[role] || ['Library'];
  }

  hasAccess(module: string): boolean {
    return this.permissionsForRole(this.userRole).includes(module);
  }

  canEdit(mod: string | { nameEn?: string; name?: string } | null | undefined): boolean {
    const moduleName = typeof mod === 'string' ? mod : (mod?.nameEn || (mod as any)?.name || '');
    if (this.userRole === 'Super Admin') return true;
    if (moduleName === 'Library' && this.userRole === 'Internal User') return true;
    return false;
  }

  /**
   * Navigate to folder - with lazy loading from API for subfolders
   */
  navigateToFolder(folderId: number | null, expandSidebar: boolean = true) {
    if (!folderId) {
      this.init();
      return;
    }

    this.currentFolderId = folderId;
    const folderInCache = this.folders.find(f => f.id === folderId);

    if (!folderInCache) {
      console.warn('Folder not found in cache, loading from API');
      return;
    }

    // Build path from current folder to root
    const path: Folder[] = [];
    let currentId: number | null = folderId;

    while (currentId) {
      const folder = this.folders.find(f => f.id === currentId);
      if (folder) {
        path.unshift(folder);
        currentId = folder.parentId;
        if (!currentId) {
          this.selectedModule = folder;
          // Set active root folder
          this.activeRootFolderId = folder.id;
        }
      } else break;
    }

    this.currentPath = path;

    // Emit folder selection event for sidebar sync
    this.fileNavigationService.selectFolder(folderInCache, path);

    // Auto-expand parent folders in sidebar if enabled
    if (expandSidebar) {
      this.expandFolderPath(folderId);
    }

    // Lazy load subfolders from API
    // Reset pagination to first page when navigating to a folder
    this.currentPage = 1;
    this.cachedCurrentFolders = [];
    this.cachedCurrentFiles = [];
    this.browseFolderFromAPI(folderId);
  }

  /**
   * Expand all parent folders in the sidebar tree to show the selected folder
   * Marks all ancestors as expanded
   */
  private expandFolderPath(folderId: number): void {
    let currentId: number | null = folderId;
    
    while (currentId) {
      // Add current folder to expanded list
      if (!this.expandedFolders.includes(currentId)) {
        this.expandedFolders.push(currentId);
      }
      
      // Move to parent
      const folder = this.folders.find(f => f.id === currentId);
      currentId = folder ? folder.parentId : null;
    }
  }

  /**
   * Navigate to a specific file in the folder hierarchy
   * Used when navigating from notification to file
   * Builds folder path using breadcrumb data
   */
  navigateToFileInFolder(fileId: number, file: FileItem, breadcrumb: Folder[], parentFolderId: number): void {
    console.log('[FileManagementComponent] Navigating to file in folder:', {
      fileId,
      fileName: file.titleEn,
      parentFolderId,
      breadcrumbLength: breadcrumb.length
    });

    // Set current folder ID
    this.currentFolderId = parentFolderId;

    // Build folder hierarchy from breadcrumb - merge with existing folders to preserve tree structure
    // Add breadcrumb folders while preserving existing root folders
    breadcrumb.forEach(folder => {
      if (!this.folders.find(f => f.id === folder.id)) {
        this.folders.push(folder);
      }
    });

    // Build current path from breadcrumb
    this.currentPath = [...breadcrumb];

    // Set selected module to root folder and mark as active
    if (breadcrumb.length > 0) {
      this.selectedModule = breadcrumb[0];
      this.activeRootFolderId = breadcrumb[0].id; // Keep root folder active when navigating to file
    }

    // Find and select the parent folder
    const parentFolder = breadcrumb.find(f => f.id === parentFolderId);
    if (parentFolder) {
      console.log('[FileManagementComponent] Selecting parent folder:', parentFolder.nameEn);
      this.fileNavigationService.selectFolder(parentFolder, breadcrumb);
    }

    // Add the file to the files array if not already present
    if (!this.files.find(f => f.id === fileId)) {
      this.files.push(file);
    }

    // Auto-expand all parent folders in the sidebar tree
    this.expandFolderPath(parentFolderId);

    // Refresh cached data to display the file
    this.refreshCachedData();

    // Highlight the file (optional visual feedback)
    this.highlightFile(fileId);

    console.log('[FileManagementComponent] File navigation completed');
  }

  /**
   * Highlight a file in the list (visual feedback)
   */
  private highlightFile(fileId: number): void {
    // Add a highlight class to the file element if it exists
    setTimeout(() => {
      const fileElement = document.querySelector(`[data-file-id="${fileId}"]`);
      if (fileElement) {
        fileElement.classList.add('highlighted');
        fileElement.scrollIntoView({ behavior: 'smooth', block: 'center' });

        // Remove highlight after 3 seconds
        setTimeout(() => {
          fileElement.classList.remove('highlighted');
        }, 3000);
      }
    }, 100);
  }

  getFolderTileIcon(folderId: number | null | undefined): string {
    // Only show selected-folder.svg for selected root folder
    const isRoot = this.rootFolders.some(f => f.id === folderId);
    const isSelectedRoot = isRoot && folderId === this.activeRootFolderId;
    return isSelectedRoot
      ? 'assets/icons/files-management/selected-folder.svg'
      : 'assets/icons/files-management/folder.svg';
  }

  getFileAsset(file: FileItem): string {
    const type = (file.type || file.titleEn || file.titleAr || '').toLowerCase();

    if (type.includes('pdf')) return 'assets/icons/files-management/pdf.svg';
    if (type.includes('doc')) return 'assets/icons/files-management/word.svg';
    if (type.includes('xls')) return 'assets/icons/files-management/excel.svg';
    if (type.includes('image') || type.includes('jpg') || type.includes('jpeg') || type.includes('png')) {
      return 'assets/icons/files-management/image.svg';
    }

    return 'assets/icons/files-management/folder.svg';
  }

  getCurrentFolderId(): number | null {
    return this.currentFolderId ?? (this.currentPath.length ? this.currentPath[this.currentPath.length - 1].id : null);
  }

  getChildFolders(parentId: number | null): Folder[] {
    if (parentId == null) {
      return this.folders.filter(f => f.parentId == null);
    }

    return this.folders.filter(f => f.parentId === parentId);
  }

  getCurrentFolders(): Folder[] {
    return this.cachedCurrentFolders;
  }

  getCurrentFiles(): FileItem[] {
    return this.cachedCurrentFiles;
  }

  // Refresh cached data when navigation/search/page changes
  refreshCachedData(): void {
    // If server-side paging is active, the API already provided the page items
    // and they were assigned to `cachedCurrentFolders`/`cachedCurrentFiles`.
    // In that case, don't overwrite them here. Otherwise compute from local data.
    if (this.serverTotalCount != null) {
      // ensure cached arrays are defined (fallback to computed values)
      this.cachedCurrentFolders = this.cachedCurrentFolders || this.getCurrentPageFolders();
      this.cachedCurrentFiles = this.cachedCurrentFiles || this.getCurrentPageFiles();
    } else {
      this.cachedCurrentFolders = this.getCurrentPageFolders();
      this.cachedCurrentFiles = this.getCurrentPageFiles();
    }
    
  }

  getAllDescendantFolderIds(folderId: number): number[] {
    const descendants: number[] = [folderId];

    const findChildren = (parentId: number) => {
      this.folders.filter(f => f.parentId === parentId).forEach(child => {
        descendants.push(child.id);
        findChildren(child.id);
      });
    };

    findChildren(folderId);
    return descendants;
  }
  hasChildren(folderId: number) {
    const folder = this.folders.find(f => f.id === folderId);
    const knownChildren = this.folders.some(f => f.parentId === folderId);
    return knownChildren || (folder?.childFolderCount ?? 0) > 0;
  }

  isExpanded(folderId: number) {
    return this.expandedFolders.includes(folderId);
  }

  toggleFolder(folderId: number, event?: Event) {
    event?.stopPropagation(); // Prevent triggering navigateToFolder
    if (this.isExpanded(folderId)) {
      this.expandedFolders = this.expandedFolders.filter(id => id !== folderId);
      return;
    }

    this.expandedFolders.push(folderId);

    // Lazy-load children when expanding if not already in cache
    if (this.getChildFolders(folderId).length === 0) {
      this.browseFolderFromAPI(folderId);
    }
  }

  editingFolderId: number | null = null;
  isRoot: boolean | null = false;

  openCreateFolder(isRoot: boolean | null = null) {
    if (!this.isInternal) return; // Block if not internal user
    this.folderForm = { nameEn: '', nameAr: '', descriptionEn: '', descriptionAr: '' };
    this.showCreateFolder = true;
    if (isRoot == true)
      this.isRoot = true;
    else
      this.isRoot = false;
  }

  openEditFolder(folder: any) {
    if (!this.isInternal) return; // Block if not internal user
    this.editingFolderId = folder.id;

    // Fill the folderForm with the folder data
    this.folderForm = {
      nameEn: folder.nameEn,
      nameAr: folder.nameAr,
      descriptionEn: folder.descriptionEn,
      descriptionAr: folder.descriptionAr
    };

    // Show the modal
    this.showCreateFolder = true;
  }


  createFolder() {
    if (!this.isInternal) return; // Block if not internal user
    if (this.editingFolderId) {
      // Update existing folder
      var folder = this.getCurrentFolders().find(f => f.id === this.editingFolderId);
      if (!folder)
        folder = this.folders.find(f => f.id === this.editingFolderId);

      if (folder) {
        folder.nameEn = this.folderForm.nameEn;
        folder.nameAr = this.folderForm.nameAr;
        folder.descriptionEn = this.folderForm.descriptionEn;
        folder.descriptionAr = this.folderForm.descriptionAr;
      }
      this.editingFolderId = null;
    } else {
      const newFolder: Folder = {
        id: this.folders.length + 1,
        nameEn: this.folderForm.nameEn,
        nameAr: this.folderForm.nameAr,
        parentId: this.isRoot == true ? null : this.getCurrentFolderId(),
        descriptionEn: this.folderForm.descriptionEn,
        descriptionAr: this.folderForm.descriptionAr,
        createdAt: new Date().toISOString(),
        childFolderCount: 0,
        fileCount: 0
      };
      this.folders.push(newFolder);
      if (this.isRoot)
        this.loadRootFolders();
    }
    this.folderForm = { nameEn: '', nameAr: '', descriptionEn: '', descriptionAr: '' };
    this.showCreateFolder = false;
    this.refreshCachedData();
  }

  editingFileId: number | null = null;


  openUploadFile() {
    if (!this.isInternal) return; // Block if not internal user
    // Reset form
    this.fileForm = {
      titleEn: '',
      titleAr: '',
      descriptionEn: '',
      descriptionAr: '',
      documentType: '',
      language: '',
      uploadDate: new Date().toISOString().split('T')[0]
    };
    this.uploadStep = 1;
    this.showUploadFile = true;
  }
  // Prefill the upload modal with file data
  editFile(file: any) {
    if (!this.isInternal) return; // Block if not internal user
    this.editingFileId = file.id;
    this.uploadStep = 1; // Start from step 1
    this.fileForm = {
      titleEn: file.titleEn,
      titleAr: file.titleAr,
      descriptionEn: file.descriptionEn,
      descriptionAr: file.descriptionAr,
      documentType: file.type,
      language: file.language,
      uploadDate: file.uploadDate,
      file: undefined
    };

    // Reset to step 1
    this.showUploadFile = true;
  }

  uploadFile() {
    if (!this.isInternal) return; // Block if not internal user
    if (this.uploadStep === 1) {
      // Move to next step (permissions)
      this.uploadStep = 2;
    } else if (this.uploadStep === 2) {
      // Get permissions from child component
      const permissions = this.permissionsTab?.getPermissions() || [];
      
      // Final submission with permissions
      const file = this.fileForm.file as File;
      const fileId = this.editingFileId ? this.editingFileId : this.files.length + 1;
      const folderId = this.getCurrentFolderId() || 1;

      // Prepare the file object with required API fields
      const fileData: FileItem = {
        id: fileId,
        titleEn: this.fileForm.titleEn || '',
        titleAr: this.fileForm.titleAr || '',
        folderId: folderId,
        type: file ? file.name.split('.').pop()?.toUpperCase() || 'FILE' : 'FILE',
        size: file ? (file.size / 1024 / 1024).toFixed(2) + ' MB' : '0 MB',
        uploadDate: this.fileForm.uploadDate || new Date().toISOString(),
        descriptionEn: this.fileForm.descriptionEn || '',
        descriptionAr: this.fileForm.descriptionAr || '',
        documentTypeId: this.fileForm.documentType || undefined,
        languageId: this.fileForm.language || undefined,
        createdAt: new Date().toISOString(),
        storageId: ''
      };

      if (this.editingFileId) {
        // Updating an existing file
        const index = this.files.findIndex(f => f.id === this.editingFileId);
        if (index !== -1) {
          this.files[index] = fileData;
        }
      } else {
        // Creating a new file
        this.files.push(fileData);
      }

      // Assign permissions to users via API (also handles revoke when permissions removed)
      if (this.permissionsTab) {
        const permissionObservable = this.permissionsTab.assignPermissionsToApi(fileId);
        
        if (permissionObservable) {
          permissionObservable.subscribe({
            next: (response: any) => {
              if (response && (response.success || Array.isArray(response))) {
                // response.success for single API call or Array for forkJoin
                this.resetUploadForm();
              } else if (response && response[0]?.success) {
                // Handle forkJoin array response
                this.resetUploadForm();
              } else {
                this.apiErrors = [response?.message || 'Failed to assign permissions'];
                this.resetUploadForm();
              }
            },
            error: (error: any) => {
              console.error('Failed to assign permissions:', error);
              this.apiErrors = ['Failed to assign permissions to users'];
              this.resetUploadForm();
            }
          });
        } else {
          // No changes in permissions
          this.resetUploadForm();
        }
      } else {
        this.resetUploadForm();
      }
    }
  }

  private resetUploadForm(): void {
    // Reset form
    this.fileForm = {
      titleEn: '',
      titleAr: '',
      descriptionEn: '',
      descriptionAr: '',
      documentType: '',
      language: '',
      uploadDate: new Date().toISOString().split('T')[0],
      file: undefined
    };
    this.uploadStep = 1;
    this.editingFileId = null;
    this.showUploadFile = false;
    this.refreshCachedData();
  }

  // Permission management methods
  goToNextStep() {
    if (this.uploadStep === 1) {
      // Validate file info step
      if (this.fileForm.titleEn && this.fileForm.titleAr && this.fileForm.documentType && this.fileForm.language) {
        this.uploadStep = 2;
      } else {
        alert('Please fill in all required fields');
      }
    }
  }

  goToPreviousStep() {
    if (this.uploadStep === 2) {
      this.uploadStep = 1;
    }
  }

  deleteFolder(folderId: number) {
    if (!this.isInternal) return; // Block if not internal user
    if (confirm('Are you sure you want to delete this folder and all its contents?')) {
      const allFolderIds = this.getAllDescendantFolderIds(folderId);
      this.folders = this.folders.filter(f => !allFolderIds.includes(f.id));
      this.files = this.files.filter(f => !allFolderIds.includes(f.folderId!));
      if (this.currentPath.some(f => allFolderIds.includes(f.id))) this.currentPath = [];
      this.init();
      this.refreshCachedData();
    }
  }

  deleteFile(fileId: number) {
    if (!this.isInternal) return; // Block if not internal user
    if (confirm('Are you sure you want to delete this file?')) {
      this.files = this.files.filter(f => f.id !== fileId);
      this.refreshCachedData();
    }
  }



  openContextMenu(event: MouseEvent | TouchEvent, folder: Folder | null = null) {
    if (!this.isInternal) return; // Block if not internal user
    event.preventDefault();
    let x = 0, y = 0;

    if (event instanceof MouseEvent) {
      x = event.clientX;
      y = event.clientY;
    } else if (event instanceof TouchEvent) {
      x = event.touches[0].clientX;
      y = event.touches[0].clientY;
    }
    this.navigateToFolder(folder!.id)
    this.contextMenu = {
      visible: true,
      x,
      y,
      folder
    };

  }

  closeContextMenu() {
    this.contextMenu.visible = false;
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    if (this.contextMenu.visible) {
      const clickedInsideContextMenu = this.contextMenuRef?.nativeElement.contains(event.target);

      if (!clickedInsideContextMenu) {
        this.closeContextMenu();
      }
    }

    if (this.openMenuFolderId != null) {
      const clickedInsideFolderMenu = this.folderMenuInfo?.nativeElement.contains(event.target);
      if (!clickedInsideFolderMenu) {
        this.closeFolderMenu();
      }
    }

    if (this.openMenuFileId != null) {
      const clickedInsideFileMenu = this.fileMenuInfo?.nativeElement.contains(event.target);
      if (!clickedInsideFileMenu) {
        this.closeFileMenu();
      }
    }
  }

  // Mobile long-press
  startPress(event: TouchEvent) {
    this.pressTimer = setTimeout(() => {
      this.openContextMenu(event);
    }, 600);
  }

  cancelPress() {
    clearTimeout(this.pressTimer);
  }


  openMenuFolderId: number | null = null;

  toggleFolderMenu(folderId: number, event: MouseEvent) {
    event.stopPropagation();
    if (this.openMenuFolderId === folderId) {
      this.openMenuFolderId = null;
    } else {
      this.openMenuFolderId = folderId;
    }
  }

  closeFolderMenu() {
    this.openMenuFolderId = null;
  }


  openMenuFileId: number | null = null;

  toggleFileMenu(fileId: number, event: MouseEvent) {
    event.stopPropagation();

    if (this.openMenuFileId === fileId) {
      this.openMenuFileId = null;
    } else {
      this.openMenuFileId = fileId;
    }
  }

  closeFileMenu() {
    this.openMenuFileId = null;
  }

  /**
   * Open file in new tab
   */
  openFile(file: FileItem): void {
    console.log(`[FileManagementComponent] Opening file: ${this.getFileTitle(file)}`);
    this.fileManagementService.openAttachmentInNewTab(file.storageId);
  }

  /**
   * Get localized folder name
   */
  getFolderName(folder: Folder): string {
    return FileManagementHelper.getFolderName(folder, this.currentLanguage);
  }

  /**
   * Get localized folder description
   */
  getFolderDescription(folder: Folder): string {
    return FileManagementHelper.getFolderDescription(folder, this.currentLanguage);
  }

  /**
   * Get localized file title
   */
  getFileTitle(file: FileItem): string {
    return FileManagementHelper.getFileTitle(file, this.currentLanguage);
  }

  /**
   * Get localized file description
   */
  getFileDescription(file: FileItem): string {
    return FileManagementHelper.getFileDescription(file, this.currentLanguage);
  }

  /**
   * Get file icon
   */
  getFileIcon(type: string): string {
    return FileManagementHelper.getFileIcon(type);
  }

  // Pagination and Search Methods
  getFilteredItems(): { folders: Folder[]; files: FileItem[] } {
    const currentFolderId = this.getCurrentFolderId();
    let folders = this.folders.filter(f => f.parentId === currentFolderId);
    let files = this.files.filter(f => f.folderId === currentFolderId);
    // console.log(folders)
    // console.log(files)

    // Server-side search is used; remove client-side filtering here.

    return { folders, files };
  }

  getPaginatedItems(): { folders: Folder[]; files: FileItem[] } {
    const { folders, files } = this.getFilteredItems();
    const allItems = [...folders, ...files];

    const startIndex = (this.currentPage - 1) * this.pageSize;
    const endIndex = startIndex + this.pageSize;
    const paginatedItems = allItems.slice(startIndex, endIndex);

    // Separate back into folders and files
    const paginatedFolders = paginatedItems.filter(item => 'descriptionEn' in item && item.hasOwnProperty('parentId')) as Folder[];
    const paginatedFiles = paginatedItems.filter(item => 'folderId' in item && item.hasOwnProperty('titleEn')) as FileItem[];

    return { folders: paginatedFolders, files: paginatedFiles };
  }

  getCurrentPageFolders(): Folder[] {
    return this.getPaginatedItems().folders;
  }

  getCurrentPageFiles(): FileItem[] {
    return this.getPaginatedItems().files;
  }

  getTotalItems(): number {
    // Prefer server-side total count when available (accurate for paged API)
    if (this.serverTotalCount != null) return this.serverTotalCount;

    const { folders, files } = this.getFilteredItems();
    return folders.length + files.length;
  }

  getTotalPages(): number {
    if (this.serverTotalPages != null) return this.serverTotalPages;
    return Math.max(1, Math.ceil(this.getTotalItems() / this.pageSize));
  }

  goToPage(page: number) {
    const maxPage = this.getTotalPages();
    if (page >= 1 && page <= maxPage) {
      this.currentPage = page;
      // If server-side paging is available, request that page from API
      const folderId = this.getCurrentFolderId() || (this.selectedModule as any)?.id || null;
      if (this.serverTotalPages != null && folderId != null) {
        this.browseFolderFromAPI(folderId, this.searchTerm);
      } else {
        this.refreshCachedData();
      }
    }
  }

  nextPage() {
    if (this.currentPage < this.getTotalPages()) {
      this.currentPage++;
      const folderId = this.getCurrentFolderId() || (this.selectedModule as any)?.id || null;
      if (this.serverTotalPages != null && folderId != null) {
        this.browseFolderFromAPI(folderId, this.searchTerm);
      } else {
        this.refreshCachedData();
      }
    }
  }

  prevPage() {
    if (this.currentPage > 1) {
      this.currentPage--;
      const folderId = this.getCurrentFolderId() || (this.selectedModule as any)?.id || null;
      if (this.serverTotalPages != null && folderId != null) {
        this.browseFolderFromAPI(folderId, this.searchTerm);
      } else {
        this.refreshCachedData();
      }
    }
  }

  onSearchChange(term: string) {
    this.searchTerm = term;
    this.currentPage = 1; // Reset to first page when search changes
    // Clear server-side totals when performing a local search filter
    this.serverTotalCount = null;
    this.serverTotalPages = null;
    const folderId = this.getCurrentFolderId();
    if (folderId) {
      this.browseFolderFromAPI(folderId, term);
    } else if (this.selectedModule && this.selectedModule.id) {
      this.browseFolderFromAPI(this.selectedModule.id, term);
    }
  }

  getPageNumbers(): number[] {
    const totalPages = this.getTotalPages();
    const pages: number[] = [];
    const maxPagesToShow = 5;

    if (totalPages <= maxPagesToShow) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      let startPage = Math.max(1, this.currentPage - 2);
      let endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);

      if (endPage - startPage < maxPagesToShow - 1) {
        startPage = Math.max(1, endPage - maxPagesToShow + 1);
      }

      for (let i = startPage; i <= endPage; i++) {
        pages.push(i);
      }
    }

    return pages;
  }

  // TrackBy functions for performance optimization
  trackByFolderId(index: number, folder: Folder): number {
    return folder.id;
  }

  trackByFileId(index: number, file: FileItem): number {
    return file.id;
  }

  trackByPageNumber(index: number, page: number): number {
    return page;
  }

  trackByPermissionId(index: number, permission: FilePermission): string | number {
    return permission.id !== undefined ? permission.id : `${permission.userId}-${index}`;
  }
}
